import { Dialog, Transition } from "@headlessui/react";
import { X } from "lucide-react";
import React, { Fragment } from "react";
import Button from "./Button";

/**
 * BaseModal component that serves as a foundation for all modals in the application.
 * Provides consistent styling and behavior for modals.
 */
const BaseModal = ({
  open,
  onClose,
  onConfirm,
  title,
  description,
  children,
  confirmText = "Potwierdź",
  cancelText = "Anuluj",
  loading = false,
  maxWidth = "sm",
  hideFooter = false,
  disableConfirm = false,
  paperClassName = "",
  hideCloseButton = false,
}) => {
  const handleClose = loading ? undefined : onClose;

  // Map MUI maxWidth to custom width classes
  const widthClasses = {
    xs: "max-w-xs",
    sm: "max-w-lg",
    md: "max-w-2xl",
    lg: "max-w-4xl",
    xl: "max-w-6xl",
  };

  const modalWidth = widthClasses[maxWidth] || widthClasses.sm;

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={handleClose || (() => {})}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={`w-full ${modalWidth} transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all ${paperClassName}`}
              >
                <div className="px-6 pt-6 pb-4 relative">
                  <Dialog.Title
                    as="h2"
                    className="text-2xl font-bold text-[#1c3f70] pr-8"
                  >
                    {title}
                  </Dialog.Title>

                  {description && (
                    <Dialog.Description className="mt-4 text-sm text-gray-500">
                      {description}
                    </Dialog.Description>
                  )}

                  {!hideCloseButton && !loading && (
                    <div className="absolute top-6 right-6">
                      <Button
                        onClick={onClose}
                        variant="outline"
                        className="!min-w-0 !px-2"
                        aria-label="Zamknij"
                      >
                        <X className="h-5 w-5 text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="px-6 py-2">{children}</div>

                {!hideFooter && (
                  <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3">
                    <Button
                      onClick={onClose}
                      disabled={loading}
                      variant="outline"
                    >
                      {cancelText}
                    </Button>

                    {onConfirm && (
                      <Button
                        onClick={onConfirm}
                        disabled={loading || disableConfirm}
                        className={"text-white"}
                      >
                        {loading ? "Przetwarzanie..." : confirmText}
                      </Button>
                    )}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default BaseModal;
